import React, { useState } from 'react';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useLovelySwitchStyles } from '@mui-treasury/styles/switch/lovely';
import {
  FormControl,
  Label,
  IDropdownItem,
  Dropdown,
} from '@uhg-harmony/uitk-react';
import styled from 'styled-components';
import { Typography } from '@material-ui/core';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Range, DateRange } from 'react-date-range';
import { BannerPlacement, DateSelection } from 'shared/widgets/Banner/types';
import EnableFullScreenWidth from '../EnableFullScreenWidth';

const labelStyles = {
  color: '#1d1929',
  'font-size': '12px',
  'font-family': 'OptumSans',
  'margin-left': '-10px',
  'padding-top': '20px',
  'font-weight': '500',
  'margin-right': '20px',
};

const typographyStyles = {
  fontSize: '12px',
  fontFamily: 'OptumSans',
};

const Wrapper = styled.div`
  display: flex;
  margin-top: 10px;
`;

const DateRangeWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  .custom-date-range {
    width: 100%;
    .rdrDateDisplayWrapper {
      cursor: pointer;
    }
    .rdrDateInput {
      pointer-events: none;
    }
  }
  .custom-date-range.collapsed {
    .rdrMonthAndYearWrapper,
    .rdrMonths {
      display: none;
    }
  }
`;

const StyledDropdown = styled(Dropdown)<{ $fullWidth?: boolean }>`
  && {
    width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  }

  & > div,
  & > div > button {
    width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  }

  .gQmwFj {
    font-family: OptumSans, sans-serif;
    width: ${props => (props.$fullWidth ? '100%' : 'fit-content')};
  }
`;

const StyledFormControl = styled(FormControl)<{ $fullWidth?: boolean }>`
  flex: ${props => (props.$fullWidth ? '1 1 0' : '0 0 150px')};
  min-width: ${props => (props.$fullWidth ? '0' : '150px')};
  width: ${props => (props.$fullWidth ? 'auto' : '150px')};

  & > div {
    width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  }

  #banner-controlled-select {
    padding-left: 0.5rem;
  }
`;

const StyledText = styled(Typography)`
  font-family: OptumSans, sans-serif;
`;

const PlacementErrorText = styled.div`
  font-family: OptumSans, sans-serif;
  font-size: 12px;
  color: #c60f13;
  margin-top: 0.25rem;
`;
PlacementErrorText.displayName = 'PlacementErrorText';

const FullScreenWrapper = styled.div`
  margin: 2rem 0 0 -10px;
  & > div > label {
    font-size: 12px;
  }
`;

const browserListItems: IDropdownItem[] = [
  { id: '1', label: 'Chrome', value: 'Chrome' },
  { id: '2', label: 'FireFox', value: 'FireFox' },
  { id: '3', label: 'Safari', value: 'Safari' },
  { id: '4', label: 'Edge', value: 'Edge' },
];

const placementItems: IDropdownItem[] = [
  { id: '1', label: 'Below Header (Inline)', value: 'Below Header (Inline)' },
  {
    id: '2',
    label: 'Sticky Bottom (Fixed to Screen)',
    value: 'Sticky Bottom (Fixed to Screen)',
  },
];

export interface BannerGeneralProps<T> {
  browserCondition: boolean;
  showonAllpage: boolean;
  browserList: string;
  onChangeBrowserList: (e: string) => void;
  onChangeBrowserCondition: (e: boolean) => void;
  onChangeDateSelection?: (e: any) => void;
  dateSelectionRange?: DateSelection;
  handleFullScreen: (data: boolean) => void;
  fullScreenEnabled: boolean;
  handleWidgetPosition: (id: string, height: number, oldHeight: number) => void;
  widgetState: Widget<T>;
  removeWidget?: (widgetId: string) => void;
  currentPage: PageVersion;
  disabled: boolean;
  placement?: BannerPlacement;
  onChangePlacement?: (value: BannerPlacement) => void;
  placementError?: string;
  allowDismiss?: boolean;
  onChangeAllowDismiss?: (value: boolean) => void;
}

export const BannerGeneralConfig: React.FC<BannerGeneralProps<any>> = (
  props: BannerGeneralProps<any>
) => {
  const {
    browserCondition,
    browserList,
    onChangeBrowserCondition,
    onChangeBrowserList,
    onChangeDateSelection,
    dateSelectionRange,
    handleFullScreen,
    fullScreenEnabled,
    disabled,
    placement,
    onChangePlacement,
    placementError,
    allowDismiss,
    onChangeAllowDismiss,
  } = props;

  const getPlacementValue = () => {
    const found = placementItems.find(item => item.value === placement);
    return found ?? placementItems[0];
  };
  const lovelyStyles = useLovelySwitchStyles();

  const getBrowserValue = () => {
    const browserDropdownValue = browserListItems.find(
      item => item.value === browserList
    );
    return browserDropdownValue ? browserDropdownValue : '';
  };

  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapse = event => {
    (event.target.classList.contains('rdrDateDisplay') ||
      event.target.classList.contains('rdrDateDisplayWrapper')) &&
      setCollapsed(prev => !prev);
  };

  const onDateSelectionChange = (item: Range) => {
    if (item.selection) {
      const currentDate = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      );
      const endDate = new Date(item.selection.endDate);
      if (currentDate !== endDate && currentDate > endDate) {
        props.handleWidgetPosition(
          props.widgetState.id,
          0,
          props.widgetState.config.height
        );
      }
    }

    setState([item.selection]);
    onChangeDateSelection(item.selection);
  };

  const onDropDownChange = (item: any) => {
    onChangeBrowserList(item.value);
  };

  const cx = (...classNames: Array<string | undefined | false>) =>
    classNames.filter(cn => !!cn).join(' ');

  const formatedDateRange = (dateRange: DateSelection) => {
    return [
      {
        startDate: new Date(dateRange.startDate),
        endDate: new Date(dateRange.endDate),
        key: 'selection',
      },
    ];
  };

  return (
    <>
      <Wrapper>
        <Label style={labelStyles}>Placement</Label>
        <StyledFormControl
          $fullWidth
          data-test-id="banner-placement-dropdown"
          disabled={disabled}
          id="banner-placement-select"
        >
          <StyledDropdown
            $fullWidth
            type="single"
            value={getPlacementValue()}
            items={placementItems}
            fieldsetLabel="Placement"
            disabled={disabled}
            onChange={(item: any) =>
              onChangePlacement?.(item.value as BannerPlacement)
            }
            data-test-id="placement-select"
            placeholderText={<StyledText>Select placement</StyledText>}
          />
        </StyledFormControl>
        {placementError && (
          <PlacementErrorText
            role="alert"
            data-test-id="banner-placement-error"
          >
            {placementError}
          </PlacementErrorText>
        )}
      </Wrapper>
      {placement === 'Sticky Bottom (Fixed to Screen)' && (
        <Wrapper>
          <Label style={labelStyles}>Allow Dismiss</Label>
          <FormControlLabel
            data-test-id="allow-dismiss-form"
            control={
              <Switch
                classes={lovelyStyles}
                checked={allowDismiss ?? false}
                disabled={disabled}
                onChange={e => onChangeAllowDismiss?.(e.target.checked)}
                data-test-id="allow-dismiss-switch"
              />
            }
            label={
              <StyledText style={typographyStyles}>
                {allowDismiss ? 'On' : 'Off'}
              </StyledText>
            }
          />
        </Wrapper>
      )}
      <FullScreenWrapper>
        <EnableFullScreenWidth
          disabled={disabled}
          handleEnableFullScreen={handleFullScreen}
          enableFullScreenWidth={fullScreenEnabled}
        />
      </FullScreenWrapper>
      <Label style={labelStyles}>Date Range</Label>
      <DateRangeWrapper onClick={toggleCollapse}>
        <DateRange
          data-test-id="date-range-select"
          disabled={disabled}
          className={cx('custom-date-range', collapsed && 'collapsed')}
          editableDateInputs={true}
          onChange={item => onDateSelectionChange(item)}
          moveRangeOnFirstSelection={false}
          ranges={
            dateSelectionRange ? formatedDateRange(dateSelectionRange) : state
          }
          minDate={new Date()}
        />
      </DateRangeWrapper>
      <Wrapper>
        <Label style={labelStyles}>Browser Conditions</Label>
        <FormControlLabel
          data-test-id="browser-condition-form"
          control={
            <Switch
              classes={lovelyStyles}
              checked={browserCondition}
              disabled={disabled}
              onChange={e => onChangeBrowserCondition(e.target.checked)}
              data-test-id="browser-switch"
            />
          }
          label={
            <StyledText style={typographyStyles}>
              {browserCondition ? 'On' : 'Off'}
            </StyledText>
          }
        />
      </Wrapper>
      {browserCondition && (
        <Wrapper>
          <Label style={labelStyles}>Select a Browser To Alert</Label>
          <StyledFormControl
            data-test-id="banner-select-dropdown"
            disabled={disabled}
            id={'banner-controlled-select'}
          >
            <StyledDropdown
              type="single"
              value={getBrowserValue()}
              items={browserListItems}
              fieldsetLabel="Browsers"
              disabled
              onChange={onDropDownChange}
              data-test-id="browser-select"
              placeholderText={<StyledText>Please Select</StyledText>}
            />
          </StyledFormControl>
        </Wrapper>
      )}
    </>
  );
};
